import { useEffect, useState } from 'react';
import { assetService } from '../../services/assetService';
import {
  buildParticleSourceImageFromMetadata,
  getParticleSourceImageCache,
  invalidateParticleSourceImageCache,
  setParticleSourceImageCache,
  type ParticleSourceImage,
} from './particleSourceImageCache';
import { fetchWorkbenchParticle, getWorkbenchParticleCache } from './ttqWorkbenchParticleCache';
import { readMultimodalAnalysisFromMetadata } from './ttqParticleMultimodalBias';

export type { ParticleSourceImage };

const inflight = new Map<string, Promise<ParticleSourceImage | null>>();

export function invalidateParticleSourceImageFetch(particleId: string): void {
  invalidateParticleSourceImageCache(particleId);
  inflight.delete(particleId);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readSourceAssetId(metadata: Record<string, unknown> | null | undefined): string {
  if (!metadata) return '';
  const extraction = asRecord(metadata['knowledgeParticleExtraction'])
    || asRecord(metadata['knowledge_particle_extraction'])
    || asRecord(metadata['particleExtraction']);
  const embedded = asRecord(metadata['relatedGraphEmbeddedImage']) || asRecord(metadata['embeddedImage']);
  const candidates: unknown[] = [
    metadata['sourceAssetId'],
    metadata['assetId'],
    metadata['asset_id'],
    extraction?.['assetId'],
    extraction?.['sourceAssetId'],
    extraction?.['asset_id'],
    embedded?.['sourceAssetId'],
    embedded?.['assetId'],
    embedded?.['asset_id'],
  ];
  for (const v of candidates) {
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

async function fetchParticleSourceImage(particleId: string): Promise<ParticleSourceImage | null> {
  const cached = getParticleSourceImageCache(particleId);
  // metadata 预填常无 ossId；仅 asset 解析完成后才直接用缓存
  if (cached !== undefined && cached !== null && cached.assetResolved) return cached;
  if (cached === null) return null;

  const existing = inflight.get(particleId);
  if (existing) return existing;

  const promise = (async (): Promise<ParticleSourceImage | null> => {
    try {
      const cachedParticle = getWorkbenchParticleCache(particleId);
      const particle = cachedParticle ?? await fetchWorkbenchParticle(particleId);
      const metadata = (particle.metadata ?? null) as Record<string, unknown> | null;
      const seeded = buildParticleSourceImageFromMetadata(metadata) || cached || null;
      let multimodalAnalysis = seeded?.multimodalAnalysis
        || readMultimodalAnalysisFromMetadata(metadata);

      let fullSourceUrl = seeded?.fullSourceUrl || seeded?.crop?.sourceImageUrl || '';
      let ossId = String(seeded?.ossId || '').trim();
      const assetId = readSourceAssetId(metadata);
      if (assetId) {
        try {
          const asset = await assetService.getAsset(assetId);
          if (asset && typeof asset.url === 'string' && asset.url.trim()) {
            // 优先用素材库当前 URL，避免 metadata 里过期直链
            fullSourceUrl = asset.url.trim();
          }
          if (typeof asset?.oss_id === 'string' && asset.oss_id.trim()) {
            ossId = asset.oss_id.trim();
          }
          const assetMeta = asRecord(asset?.metadata);
          multimodalAnalysis = readMultimodalAnalysisFromMetadata(assetMeta) || multimodalAnalysis;
        } catch {
          // 忽略
        }
      }

      const crop = seeded?.crop
        ? {
          ...seeded.crop,
          sourceImageUrl: fullSourceUrl || seeded.crop.sourceImageUrl,
        }
        : undefined;

      const record: ParticleSourceImage | null = (crop || fullSourceUrl || multimodalAnalysis || ossId)
        ? {
          ...(seeded || {}),
          crop,
          fullSourceUrl: fullSourceUrl || seeded?.fullSourceUrl || seeded?.crop?.sourceImageUrl,
          ossId: ossId || undefined,
          assetResolved: true,
          multimodalAnalysis: multimodalAnalysis ?? null,
        }
        : null;
      setParticleSourceImageCache(particleId, record);
      return record;
    } catch {
      return null;
    } finally {
      inflight.delete(particleId);
    }
  })();

  inflight.set(particleId, promise);
  return promise;
}

export function useParticleEmbeddedImage(
  particleId: string | undefined,
  enabled: boolean = true,
): { data: ParticleSourceImage | null; loading: boolean } {
  const initial = particleId ? getParticleSourceImageCache(particleId) : undefined;
  const [record, setRecord] = useState<ParticleSourceImage | null>(
    initial === undefined ? null : initial,
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!particleId || !enabled) return;
    const hit = getParticleSourceImageCache(particleId);
    if (hit !== undefined && (hit === null || hit.assetResolved)) {
      setRecord(hit);
      return;
    }
    if (hit) setRecord(hit);
    let active = true;
    setLoading(true);
    void fetchParticleSourceImage(particleId).then((r) => {
      if (!active) return;
      setRecord(r);
      setLoading(false);
    });
    return () => { active = false; };
  }, [particleId, enabled]);

  return { data: record, loading };
}
